import React, { useState, useEffect, useRef } from 'react'
import { FaExclamationTriangle, FaCheck, FaTimes, FaCog } from 'react-icons/fa'
import { Progress, FormControl, Badge, Icon, Box, Stack, Heading, Text, Button, Input, Select, Tag, Menu, MenuItem, ConfirmButton, SimplePopover, SimpleTable, UserChip } from '@/components/lib'
import utils, { TEAM_PERMISSIONS } from '../../utils/utils.js'
import api from '../../api'
import useStore from '../../store'

export default function TeamUsers () {
  const [creating, setCreating] = useState(false)
  const [newUserEmail, setNewUserEmail] = useState('')
  const [members, setMembers] = useState([])
  const [page, setPage] = useState(1)
  const [filterTerm, setFilterTerm] = useState('')
  const [sort, setSort] = useState('firstName|asc')
  const [loading, setLoading] = useState(false)
  const { user, team } = useStore()

  const getUsers = useRef(utils.debounce((teamId, page, term, sortKey, sortOrder) => {
    api.teams.getUsers(teamId, page, term, sortKey, sortOrder, d => {
      setMembers(d.users)
      setLoading(false)
    }, () => {
      setLoading(false)
    })
  }, 500)).current

  useEffect(() => {
    if (!team?._id) return
    const [sortKey, sortOrder] = sort.split('|')
    setLoading(true)
    getUsers(team._id, page, filterTerm, sortKey, sortOrder)
  }, [team?._id, page, filterTerm, sort, getUsers])

  function create () {
    setCreating(true)
    api.teams.createUser(team._id, { email: newUserEmail, sendInvite: true }, result => {
      if (result.crossTeamInvitationSent) {
        utils.toast('success', 'Invitation sent', `${result.email} already has a Trialflare account. We've sent them an email to join your team.`)
      } else {
        utils.toast('success', 'User invited', 'We\'ve invited the person to your team')
        const newMembers = Object.assign([], members)
        newMembers.push(result)
        setMembers(newMembers)
      }
      setCreating(false)
      setNewUserEmail('')
    }, err => {
      utils.toast('error', 'Unable to create user', err.message)
      setCreating(false)
    })
  }

  const togglePermission = (userId, permission) => {
    const scope = team._id
    const member = members.find(m => m._id === userId)
    if (!member) return
    if (!member.permissions[scope]) member.permissions[scope] = []
    const permissionIndex = member.permissions[scope]?.indexOf(permission)
    console.log(member.permissions[scope], permission, permissionIndex)
    const permissions = permissionIndex === -1
      ? member.permissions[scope].concat(permission)
      : member.permissions[scope].filter(p => p !== permission)
    api.users.updatePermissions(userId, scope, permissions, data => {
      setMembers(Object.assign([], members).map(u => {
        if (u._id === userId) u.permissions = Object.assign({}, { [scope]: data.permissions })
        return u
      }))
    }, err => {
      utils.toast('error', 'Unable to update user', err.message)
    })
  }
  const deleteUser = (userId) => {
    api.users.delete(userId, () => {
      setMembers(members.filter(m => m._id !== userId))
      utils.toast('info', 'User deleted')
    }, err => {
      utils.toast('error', 'Unable to delete user', err.message)
    })
  }
  const disableUser = (userId, reason) => {
    api.users.disable(userId, reason, () => {
      setMembers(members.map(m => m._id === userId ? { ...m, disabled: { reason } } : m))
      utils.toast('info', 'Account disabled', 'They are signed out and cannot sign in until re-enabled.')
    }, err => utils.toast('error', 'Unable to disable the account', err.message))
  }
  const enableUser = (userId) => {
    api.users.enable(userId, () => {
      setMembers(members.map(m => m._id === userId ? { ...m, disabled: null } : m))
      utils.toast('info', 'Account re-enabled')
    }, err => utils.toast('error', 'Unable to re-enable the account', err.message))
  }
  const resetMfa = (userId) => {
    api.users.resetMfa(userId, () => {
      setMembers(members.map(m => {
        if (m._id === userId) return Object.assign({}, m, { otpEnabled: false, hasPasskey: false })
        return m
      }))
      utils.toast('info', 'Two-factor authentication reset. The user will set it up again when they next sign in.')
    }, err => {
      utils.toast('error', 'Unable to reset two-factor authentication', err.message)
    })
  }
  const resetLoginAttempts = (userId) => {
    if (!window.confirm('Really reset the incorrect logins for this user?')) return
    api.users.update(userId, { incorrectLoginAttempts: null }, newUser => {
      setMembers(members.map(m => {
        if (m._id === userId) return Object.assign({}, m, { incorrectLoginAttempts: null })
        return m
      }))
      utils.toast('info', 'User updated')
    }, err => {
      utils.toast('error', 'Unable to update user', err.message)
    })
  }

  return (
    <Stack gap={5}>
      <Stack direction='row' justifyContent='space-between'>
        <Box>
          <Heading as='h3' size='md'>Users in {team?.name || 'your team'}</Heading>
          <Text>The users listed below are members of your team.</Text>
        </Box>
        <SimplePopover
          on='hover'
          trigger={<Button size='sm' variant='outline'>Adding users to a trial</Button>}
          header='Adding users to a trial'
          body={
            <Stack>
              <Text>In order for a team member to get access to a trial, they need to be <strong>added as personnel</strong> in the trial's settings. The team member list shows all users belonging to the team even if they don't have access to any trials.</Text>
              <Text>Please note that users with the "manage any trial" permission can access all trials in the team without being added as trial personnel.</Text>
            </Stack>
}
        />
      </Stack>

      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Stack direction='row'>
          <Input placeholder='Filter users...' value={filterTerm} onChange={e => { setFilterTerm(e.target.value); setPage(1) }} />
          <Select value={sort} onChange={e => { setSort(e.target.value); setPage(1) }}>
            <option value='firstName|asc'>First name (A-Z)</option>
            <option value='firstName|desc'>First name (Z-A)</option>
            <option value='createdAt|asc'>Oldest first</option>
            <option value='createdAt|desc'>Newest first</option>
          </Select>
        </Stack>

        {utils.hasPermission(user, 'users.create', team?._id) &&
          <Box display='flex' justifyContent='end'>
            <SimplePopover
              placement='left'
              on='hover'
              trigger={<Button colorPalette='primary'>Add a new user</Button>}
              header={
                <Box>
                  <Heading size='md' as='h4'>Invite someone to join {team.name}</Heading>
                  <Text>Inviting users allows them to help out with managing trials or your team account.</Text>
                </Box>
}
              body={
                <Stack gap={3}>
                  <FormControl label='Work email address' isRequired>
                    <Input autoFocus type='email' value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder='jane@example.com' />
                  </FormControl>
                  <Button colorPalette='primary' onClick={create} isLoading={creating}>Send invitation</Button>
                </Stack>
}
              footer={
                <Text>After you invite them, we'll send an email to the new user asking them to set-up their account.</Text>
}
            />
          </Box>}
      </Stack>

      {loading && <Progress size='xs' value={null} />}
      <SimpleTable
        tableProps={{ size: 'sm', variant: 'striped' }}
        headers={['Name', 'Status', 'Registered', 'Last seen', '']}
        rows={
          members?.map(m => {
            let allPermissions = 0
            Object.keys(m?.permissions || {})?.forEach(scope => {
              allPermissions += m.permissions[scope]?.length || 0
            })

            const userItems = []
            if (utils.hasPermission(user, 'users.manage', user.team)) {
              TEAM_PERMISSIONS.forEach(r => {
                userItems.push(
                  <MenuItem key={r.key} value={r.key} onClick={e => togglePermission(m._id, r.key)}>
                    {m.permissions[user.team]?.includes(r.key) ? <Icon as={FaCheck} color='fg.success' /> : <Icon as={FaTimes} color='fg.muted' />}
                    <Text ml={2}>{r.description}</Text>
                  </MenuItem>
                )
              })
            }
            if (utils.hasPermission(user, 'users.manage', user.team) && m._id !== user._id && (m.otpEnabled || m.hasPasskey)) {
              userItems.push(
                <ConfirmButton
                  asSubMenu
                  key='resetMfa'
                  trigger='Reset two-factor authentication'
                  header={`Reset two-factor authentication for ${m.firstName || 'this user'}?`}
                  footer='Their authenticator app and passkeys are removed and their sessions signed out. They will be asked to set up a new second factor the next time they sign in. Use this when someone has lost the device they sign in with.'
                  onConfirm={() => resetMfa(m._id)}
                  closeOnBlur={false}
                />
              )
            }
            if (utils.hasPermission(user, 'users.manage', user.team) && m._id !== user._id) {
              userItems.push(m.disabled
                ? (
                  <ConfirmButton
                    asSubMenu
                    key='enable'
                    trigger='Re-enable account'
                    header={`Re-enable ${m.firstName || 'this user'}'s account?`}
                    footer='They will be able to sign in again with the permissions they still hold.'
                    onConfirm={() => enableUser(m._id)}
                    closeOnBlur={false}
                  />
                  )
                : (
                  <ConfirmButton
                    asSubMenu
                    key='disable'
                    reasonRequired
                    reasonLabel='Reason for disabling'
                    trigger='Disable account'
                    header={`Disable ${m.firstName || 'this user'}'s account?`}
                    body='This user will be auto-logged-out and will not be able to login again until their account is re-enabled. For data consistency, we recommend this over deleting accounts when a team member leaves.'
                    onConfirm={reason => disableUser(m._id, reason)}
                    closeOnBlur={false}
                  />
                  ))
            }
            if (utils.hasPermission(user, 'users.delete', user.team)) {
              userItems.push(
                <ConfirmButton
                  asSubMenu
                  trigger='Delete user'
                  header={`Really delete ${m.firstName || 'this user'}'s account?`}
                  footer='This action cannot be undone, however the user can then be invited again. To take someone off the system while keeping their history, disable the account instead.'
                  onConfirm={() => deleteUser(m._id)}
                  closeOnBlur={false}
                />
              )
            }
            return [
              <UserChip compact user={m} key={m._id} />,
              <Stack direction='row' key={m._id}>
                {m.invitePending &&
                  <Tag colorPalette='primary' size='sm' ml={2}>Invited {utils.formatDateTime(m.invitedAt)}</Tag>}
                {utils.hasPermission(m, 'root', 'global') && <Tag size='sm' ml={2} colorPalette='blue'>Account manager</Tag>}
                {m.disabled && <Tag size='sm' ml={2} colorPalette='red' title={m.disabled.reason}>Disabled</Tag>}
                {allPermissions === 0 && <Tag colorPalette='orange' size='sm' ml={2}><Icon as={FaExclamationTriangle} mr={2} /> This user has no permissions</Tag>}
                {m.incorrectLoginAttempts > 0 &&
                  <Badge size='xs' colorPalette='red' alignItems='center'>
                    {m.incorrectLoginAttempts} login attempts
                    <Icon as={FaTimes} ml={2} cursor='pointer' onClick={e => resetLoginAttempts(m._id)} />
                  </Badge>}
                {m.hasPassword && !m.otpEnabled && !m.hasPasskey && <Tag size='sm' colorPalette='orange'><Icon mr={1} as={FaExclamationTriangle} /> 2FA not set up</Tag>}
              </Stack>,
              utils.formatDateTime(m.createdAt),
              m.lastSeenAt ? utils.formatDateTime(m.lastSeenAt) : 'Never',
              <Box key={m._id}>
                {(utils.hasPermission(user, 'users.manage', user.team) || utils.hasPermission(user, 'users.delete', user.team)) &&
                  <Menu
                    menuProps={{ closeOnSelect: false }}
                    trigger={<Button size='sm' variant='outline'><Icon as={FaCog} /></Button>}
                  >
                    {userItems}
                  </Menu>}
              </Box>
            ]
          }) || []
}
      />

      <Stack direction='row' justifyContent='center' mt={5}>
        <Button size='sm' onClick={() => setPage(page - 1)} isDisabled={page === 1}>Previous page</Button>
        <Button size='sm' onClick={() => setPage(page + 1)} isDisabled={members?.length < 30}>Next page</Button>
      </Stack>
    </Stack>
  )
}
